import React, { useState, useEffect } from 'react';
import { FileText, Upload, Link as LinkIcon, ImagePlus, Bot, Sparkles, FileUp, PenLine, Loader2, Wand2, File, FileCode, Coins, X, HelpCircle, Download, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { TemplateSelector } from './TemplateSelector';
import { LoadingAnimation } from './LoadingAnimation';
import { CV_TEMPLATES } from '../lib/cvTemplates';
import { extractTextFromFile } from '../lib/pdfExtractor';
import { extractTextFromCV, hasValidText } from '../services/ocrService';
import { scrapeJobDescription } from '../lib/jobScraper';
import { generateOptimizedCV, getJobDetails, generateCoverLetter, generateImprovementAnalysis } from '../lib/groq';
import { useNavigate } from 'react-router-dom';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ImageCropper from './ImageCropper';
import { Session } from '@supabase/supabase-js';
import { getUserCoins, updateUserCoins, hasEnoughCoins, getUserTransactions, CoinTransaction, supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import WelcomeSection from './WelcomeSection';
import UserAvatar from './UserAvatar';
import Folder from './Folder';
import { PaykuService } from '../services/paykuService';
import { getUserSavedCV, saveUserCV, deleteUserSavedCV } from '../lib/userCV';
import { getUserProfilePhoto, saveUserProfilePhoto } from '../lib/userPhoto';
import { createCVGeneration, updateCVGeneration, generateCVName } from '../lib/cvGenerations';

interface DashboardProps {
  session: Session | null;
}

// Función para formatear números con separador de miles utilizando puntos
const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const PhotoPreview = ({ photo, onDelete }: { photo: string | null, onDelete: () => void }) => {
  return (
    <div className="relative group">
      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-800 border-2 border-gray-700">
        {photo ? (
          <>
            <img
              src={photo}
              alt="Foto de perfil"
              className="w-full h-full object-cover"
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                onDelete();
              }}
              className="absolute top-0 right-0 p-2 bg-red-500 rounded-full transform translate-x-1/3 -translate-y-1/3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
              title="Eliminar foto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full hover:bg-gray-700 transition-colors">
            <ImagePlus className="w-8 h-8 text-blue-400 mb-2" />
            <span className="text-sm text-gray-300">Subir foto</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para el bot amigable
const FriendlyBot = ({ message }: { message: string }) => {
  return (
    <div className="mt-4 animate-fadeIn">
      <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-purple-500/30">
        <div className="relative flex-shrink-0">
          {/* Efectos de fondo circulares */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-40 blur-md transform scale-125"></div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 opacity-30 blur-sm transform scale-115"></div>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center text-white shadow-lg relative z-10">
            <Bot className="w-5 h-5" />
          </div>
        </div>
        <div className="flex-1">
          <p className="text-sm text-purple-100 my-auto">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

// Componente para el icono 3D de moneda - ELIMINADO
const CoinSplineIcon = () => {
  return null;
};

// Componente para el Modal de Recarga
interface RechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const RechargeModal: React.FC<RechargeModalProps> = ({ isOpen, onClose, userId }) => {
  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Estado para controlar la ventana de confirmación
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [packageToConfirm, setPackageToConfirm] = useState<any>(null);

  // Paquetes disponibles
  const coinPackages = [
    { id: 'basic', name: 'Starter', coins: 100, price: 3990, popularidad: 'baja', discount: 25, cvs: 1 },
    { id: 'standard', name: 'Estándar', coins: 300, price: 7490, popularidad: 'media', recommended: true, discount: 50, cvs: 3 },
    { id: 'premium', name: 'Advanced', coins: 1000, price: 9990, popularidad: 'alta', discount: 75, cvs: 10, bestValue: true }
  ];

  // Función para navegar a la página de monedas con el paquete seleccionado
  const handleNavigateToCoins = (packageId: string = '') => {
    // Si se seleccionó un paquete específico, añadimos su ID como parámetro
    const url = packageId ? `/coins?package=${packageId}` : '/coins';
    window.location.href = url;
  };

  // Función para mostrar la ventana de confirmación
  const handleSelectPackage = (packageId: string) => {
    setSelectedPackage(packageId);
    // Encuentra el paquete seleccionado y establécelo como packageToConfirm
    const selectedPkg = coinPackages.find(pkg => pkg.id === packageId);
    setPackageToConfirm(selectedPkg);
    setShowConfirmation(true);
  };

  // Función para realizar la compra después de confirmar
  const handleConfirmPurchase = async () => {
    if (!selectedPackage || !userId) return;

    setIsLoading(true);

    try {
      const selectedPkg = coinPackages.find(pkg => pkg.id === selectedPackage);
      if (!selectedPkg) {
        toast.error('Paquete no encontrado');
        setIsLoading(false);
        return;
      }

      // Obtener la URL base de la aplicación (para construcción de URLs)
      const baseUrl = window.location.origin;

      // Guardar el ID del usuario actual en localStorage
      localStorage.setItem('current_user_id', userId);

      // Crear transacción usando PaykuService
      const paykuService = PaykuService.getInstance();
      const response = await paykuService.createTransaction(
        userId,
        selectedPkg.id,
        selectedPkg.price,
        selectedPkg.coins
      );

      console.log('Respuesta de creación de transacción:', response);

      if (response && response.success && response.payment_url) {
        // Guardar información de la transacción en sessionStorage para recuperarla después
        const transactionData = {
          id: response.transaction_id || '',
          package_id: selectedPkg.id,
          amount: selectedPkg.price,
          coins: selectedPkg.coins,
          timestamp: Date.now(),
          user_id: userId,
          return_url: `${window.location.origin}/verify-payment?txid=${response.transaction_id || ''}&status=success`
        };

        sessionStorage.setItem('pendingTransaction', JSON.stringify(transactionData));
        localStorage.setItem('pendingTransaction', JSON.stringify(transactionData));

        // Guardar el último ID de transacción para verificación posterior
        localStorage.setItem('last_payku_transaction', response.transaction_id || '');

        // Mostrar toast avisando que se redirigirá al usuario
        toast.success(
          'Redirigiendo a la página de pago...',
          { duration: 2000 }
        );

        // Pequeña pausa para que el usuario vea el mensaje
        setTimeout(() => {
          // **** FIX: Check if payment_url exists before redirecting ****
          if (response.payment_url) {
            window.location.href = response.payment_url;
          } else {
            // Handle the case where URL is missing despite success=true
            console.error("Payku response success but payment_url is missing.");
            toast.error("Error al obtener la URL de pago. Inténtalo de nuevo.");
            setIsLoading(false); // Reset loading state if redirect fails
          }
        }, 1000);
      } else {
        toast.error(response?.message || 'No se pudo crear la transacción');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error al procesar la compra:', error);
      toast.error('Hubo un error al procesar la compra. Inténtalo de nuevo.');
      setIsLoading(false);
    }
  };

  const handleCancelPurchase = () => {
    setShowConfirmation(false);
    setSelectedPackage(null);
  };

  // Contenido de la ventana de confirmación
  const renderConfirmationModal = () => {
    if (!showConfirmation || !packageToConfirm) return null;

    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[60] animate-fadeIn">
        <div className="relative z-10 w-full max-w-md mx-auto bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-xl border border-blue-500/30 shadow-2xl">
          {/* Efectos de fondo */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-purple-500/10 rounded-full blur-xl"></div>

          {/* Encabezado de confirmación */}
          <div className="text-center mb-6">
            <div className="inline-block p-3 bg-blue-600/20 rounded-full mb-3">
              <Coins className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Confirmar compra</h3>
            <p className="text-gray-300 text-sm mt-1">
              Estás a punto de realizar el siguiente pago
            </p>
          </div>

          {/* Detalles del paquete */}
          <div className="bg-gray-800/80 rounded-lg p-4 mb-5 border border-gray-700">
            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">Paquete</span>
              <span className="font-semibold text-white">{packageToConfirm.name}</span>
            </div>

            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">Monedas</span>
              <span className="font-semibold text-yellow-400">{packageToConfirm.coins} monedas</span>
            </div>

            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">CVs que podrás generar</span>
              <span className="font-semibold text-green-400">Hasta {packageToConfirm.cvs} CV{packageToConfirm.cvs > 1 ? 's' : ''}</span>
            </div>

            {packageToConfirm.discount > 0 && (
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
                <span className="text-gray-300">Descuento</span>
                <span className="font-semibold text-green-400">{packageToConfirm.discount}% OFF</span>
              </div>
            )}

            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-300 font-medium">Precio final</span>
              <span className="text-xl font-bold text-white">${packageToConfirm.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</span>
            </div>
          </div>

          {/* Información de pago */}
          <div className="bg-blue-900/20 rounded-lg p-3 mb-5 text-sm">
            <p className="text-blue-200 flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Serás redirigido a WebPay para realizar el pago de forma segura. Una vez completado, las monedas se añadirán automáticamente a tu cuenta.
              </span>
            </p>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <button
              onClick={handleCancelPurchase}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>

            <button
              onClick={handleConfirmPurchase}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Confirmar pago</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn overflow-hidden">
      {/* Overlay para cerrar al hacer clic fuera */}
      <div className="absolute inset-0" onClick={onClose}></div>

      {/* Contenedor con scroll */}
      <div className="relative z-10 w-full max-h-[90vh] overflow-y-auto px-4 py-2 md:px-0 md:py-0 no-scrollbar">
        <div className="bg-gradient-to-br from-gray-900 via-blue-900/90 to-gray-900 rounded-xl p-4 md:p-6 max-w-lg mx-auto relative border border-purple-500/30 shadow-xl">
          {/* Efectos de fondo */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>

          {/* Botón de cierre - Mejorado para móviles */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-white bg-gray-800/80 hover:bg-gray-700 p-1.5 rounded-full transition-colors z-20"
            aria-label="Cerrar"
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Encabezado */}
          <div className="text-center mb-4 md:mb-6 relative z-10">
            <div className="inline-block p-2.5 rounded-full bg-yellow-500/20 mb-3">
              <Coins className="h-8 w-8 text-yellow-400" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">¡Saldo insuficiente!</h2>
            <p className="text-gray-300 text-sm md:text-base max-w-md mx-auto">
              Necesitas <span className="font-medium text-yellow-400">100 monedas</span> para generar un CV optimizado con IA.
            </p>
          </div>

          {/* Mensajes de error */}
          {errorMessage && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4">
              <p className="text-red-300 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Paquetes de monedas - Adaptados para móvil */}
          <div className="grid grid-cols-1 gap-3 mb-4 md:mb-6">
            {coinPackages.map((pkg) => {
              const isRecommended = pkg.id === 'standard';
              const isPremium = pkg.id === 'premium';
              const isSelected = selectedPackage === pkg.id;

              return (
                <div
                  key={pkg.id}
                  onClick={() => !isLoading && handleSelectPackage(pkg.id)}
                  className={`relative bg-gray-800/60 backdrop-blur-sm rounded-lg overflow-hidden transition-all hover:transform hover:scale-[1.02] border cursor-pointer ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                    } ${isSelected
                      ? 'border-green-500 shadow-md shadow-green-500/20'
                      : isRecommended
                        ? 'border-blue-500/50 shadow-md shadow-blue-500/20'
                        : isPremium
                          ? 'border-purple-500/50 shadow-md shadow-purple-500/20'
                          : 'border-gray-600/50'
                    }`}
                >
                  {/* Contenido principal */}
                  <div className="flex items-center p-3 relative z-10">
                    {/* Imagen e información */}
                    <div className="flex-shrink-0 mr-3">
                      <div className={`rounded-full p-1.5 ${isSelected
                        ? 'bg-green-400/30'
                        : 'bg-yellow-400/20'
                        }`}>
                        <Coins className={`w-5 h-5 ${isSelected
                          ? 'text-green-400'
                          : 'text-yellow-400'
                          }`} />
                      </div>
                    </div>

                    {/* Detalles */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-base font-bold text-white">{pkg.name}</h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-lg font-bold text-yellow-400">{pkg.coins}</span>
                            <span className="text-yellow-300/70 text-xs">monedas</span>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-lg font-bold text-white">${pkg.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</div>
                          {pkg.discount > 0 && (
                            <div className="text-xs text-green-400">
                              {pkg.discount}% OFF
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Etiqueta inferior */}
                  {(isRecommended || isPremium) && (
                    <div className={`py-1 px-3 text-center text-xs font-medium ${isRecommended
                      ? 'bg-blue-600 text-white'
                      : isPremium
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300'
                      }`}>
                      {isRecommended ? 'MÁS POPULAR' : isPremium ? 'MEJOR VALOR' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mensaje de instrucciones */}
          <div className="bg-gray-800/70 rounded-lg p-3 mb-4 text-sm text-center">
            <p className="text-gray-300">
              {isLoading
                ? 'Procesando tu solicitud, serás redirigido a la pasarela de pago...'
                : 'Haz clic en un paquete para comprar directamente.'}
            </p>
          </div>

          {/* Botones */}
          <div className="text-center">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={`w-full mt-3 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>

      {/* Ventana de confirmación */}
      {renderConfirmationModal()}
    </div>
  );
};

export function Dashboard({ session }: DashboardProps) {
  const navigate = useNavigate();

  // Estados
  const [cvText, setCvText] = useState('');
  const [showCvTextArea, setShowCvTextArea] = useState(false);
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [saveAsProfilePhoto, setSaveAsProfilePhoto] = useState(false); // Toggle para guardar como foto de perfil
  const [selectedTemplate, setSelectedTemplate] = useState<string>('template1');
  const [selectedTemplateColor, setSelectedTemplateColor] = useState<string>('#4B6A9B');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [cropConfig, setCropConfig] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100
  });
  const [tempImage, setTempImage] = useState<string>('');
  const [showCropper, setShowCropper] = useState(false);
  const [currentCrop, setCurrentCrop] = useState<Crop | null>(null);
  const [templateCategory, setTemplateCategory] = useState('all');
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [fileSize, setFileSize] = useState<string>('');
  const [userCoins, setUserCoins] = useState<number | null>(null);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [lastRecharge, setLastRecharge] = useState<string>("Nunca");
  const [spentThisMonth, setSpentThisMonth] = useState<number>(0);
  const [showFullCvText, setShowFullCvText] = useState(false);
  const [showCvPreloadedBanner, setShowCvPreloadedBanner] = useState(false);

  // Estado para el modal de recarga
  const [showRechargeModal, setShowRechargeModal] = useState(false);

  // Estados para controlar la secuencia y mensajes del bot
  const [activeSection, setActiveSection] = useState(1);
  const [section1Complete, setSection1Complete] = useState(false);
  const [section2Complete, setSection2Complete] = useState(false);
  const [section3Complete, setSection3Complete] = useState(false);
  const [section4Complete, setSection4Complete] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  const [showHelpSection, setShowHelpSection] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);

  // --- NUEVO useEffect para hacer scroll al cambiar de sección ---
  useEffect(() => {
    // Hacer scroll a la parte superior de la página con animación suave
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activeSection]); // Se ejecuta cada vez que activeSection cambia
  // --- Fin del nuevo useEffect ---

  // Efecto para controlar la secuencia basada en la compleción de secciones
  useEffect(() => {
    // Sección 1 completada cuando hay texto de CV
    if (cvText && !section1Complete) {
      setSection1Complete(true);
    } else if (!cvText) {
      setSection1Complete(false);
    }

    // Sección 2 completada cuando hay descripción del trabajo
    if (jobDescription && !section2Complete) {
      setSection2Complete(true);
    } else if (!jobDescription) {
      setSection2Complete(false);
    }

    // Sección 3 completada cuando hay foto O cuando el usuario decide continuar sin foto
    // (section3Complete se establece manualmente con el botón "Continuar sin foto")
    if (photo && !section3Complete) {
      setSection3Complete(true);
    } else if (!photo && section3Complete) {
      // Si no hay foto pero la sección está marcada como completa, mantenemos el estado
      // Esto permite que el usuario continúe sin foto
    } else if (!photo && !section3Complete) {
      // Si no hay foto y la sección no está completa, esperamos la acción del usuario
    }

    // Sección 4 completada cuando se selecciona una plantilla - Eliminamos esta parte
    // para que solo se complete cuando el usuario selecciona explícitamente un diseño
    if (!selectedTemplate) {
      setSection4Complete(false);
    }
  }, [cvText, jobDescription, photo, selectedTemplate, section1Complete, section2Complete, section3Complete]);

  // Efecto para cargar las monedas del usuario al inicio
  useEffect(() => {
    if (session?.user) {
      loadUserCoins();
      loadTransactions();
    }
  }, [session, refreshTrigger]);

  // Ref para controlar la carga inicial de datos y evitar duplicados
  const userDataLoadedRef = React.useRef(false);

  // Auto-cargar CV guardado y foto de perfil
  useEffect(() => {
    const loadUserData = async () => {
      // Si ya se cargaron los datos o no hay sesión, salir
      if (!session?.user?.id || userDataLoadedRef.current) return;

      try {
        // Verificar si ya tenemos datos cargados en el estado para evitar re-carga
        if (cvText) {
          userDataLoadedRef.current = true;
          return;
        }

        const cvResult = await getUserSavedCV(session.user.id);

        // Marcar como cargado ANTES de mostrar toast para evitar race conditions
        userDataLoadedRef.current = true;

        if (cvResult.success && cvResult.data && !cvText) {
          setCvText(cvResult.data.cv_text);
          setFileName(cvResult.data.cv_filename || 'CV guardado');

          // Mostrar banner de CV pre-cargado
          setShowCvPreloadedBanner(true);

          // Ocultar el banner después de 2 segundos
          setTimeout(() => {
            setShowCvPreloadedBanner(false);
          }, 2000);
        }

        // Cargar foto de perfil con sistema de prioridad:
        // 1. Foto del Storage de Supabase (la del Header) - MÁXIMA PRIORIDAD
        // 2. Foto de la tabla user_profile_photo
        // 3. Foto de Gmail (google) - Solo si no hay foto en las anteriores

        // Prioridad 1: Intentar cargar desde Storage (misma ubicación que el Header)
        try {
          const { data: existingAvatar, error: existingError } = await supabase
            .storage
            .from('curriculab')
            .list(`avatars/${session.user.id}`);

          if (!existingError && existingAvatar && existingAvatar.length > 0) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('curriculab')
              .getPublicUrl(`avatars/${session.user.id}/${existingAvatar[0].name}`);

            setPhoto(publicUrl);
            console.log('📸 Foto cargada desde Storage (misma del Header)');
          } else {
            // Prioridad 2: Intentar cargar desde la tabla user_profile_photo
            const photoResult = await getUserProfilePhoto(session.user.id);

            if (photoResult.success && photoResult.data) {
              setPhoto(photoResult.data.photo_url);
              console.log(`📸 Foto cargada desde tabla: ${photoResult.data.photo_source}`);
            } else {
              // Prioridad 3: Intentar obtener desde Google como último recurso
              const userPhotoUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.photo || null;

              if (userPhotoUrl) {
                setPhoto(userPhotoUrl);
                // Guardar automáticamente la foto de Google (source: 'google')
                await saveUserProfilePhoto(session.user.id, userPhotoUrl, 'google');
                console.log('📸 Foto de Gmail guardada como inicial');
              }
            }
          }
        } catch (error) {
          console.error('Error al cargar foto desde Storage:', error);
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
      }
    };

    loadUserData();
  }, [session]); // Dependencia solo en session

  // Función para cargar las monedas del usuario desde la base de datos
  const loadUserCoins = async () => {
    if (!session?.user) return;

    setLoadingCoins(true);
    try {
      const coins = await getUserCoins(session.user.id);
      if (coins) {
        setUserCoins(coins.balance || 0);
      }
    } catch (error) {
      console.error('Error al cargar las monedas:', error);
    } finally {
      setLoadingCoins(false);
    }
  };

  // Función para cargar las transacciones del usuario
  const loadTransactions = async () => {
    if (!session?.user) return;

    setLoadingTransactions(true);
    try {
      const txs = await getUserTransactions(session.user.id);
      setTransactions(txs);

      // Calcular última recarga y gasto mensual
      calculateLastRecharge(txs);
      calculateMonthlySpending(txs);
    } catch (error) {
      console.error('Error al cargar las transacciones:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Función para calcular la última recarga
  const calculateLastRecharge = (txs: CoinTransaction[]) => {
    const recharges = txs.filter(tx => tx.amount > 0);
    if (recharges.length > 0) {
      const mostRecent = recharges[0];
      setLastRecharge(formatDate(mostRecent.created_at));
    } else {
      setLastRecharge("Nunca");
    }
  };

  // Función para calcular el gasto mensual
  const calculateMonthlySpending = (txs: CoinTransaction[]) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const expenses = txs.filter(tx => {
      const txDate = new Date(tx.created_at);
      return tx.amount < 0 && txDate >= startOfMonth;
    });

    const totalSpent = expenses.reduce((total, tx) => total + Math.abs(tx.amount), 0);
    setSpentThisMonth(totalSpent);
  };

  // Formato de fecha para mostrar
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Función para manejar el cambio de sección manualmente
  const handleSectionChange = (sectionNumber: number) => {
    // Solo permitir cambiar a secciones anteriores o a la siguiente disponible
    if (sectionNumber < activeSection ||
      (sectionNumber === activeSection + 1 &&
        ((sectionNumber === 2 && section1Complete) ||
          (sectionNumber === 3 && section2Complete) ||
          (sectionNumber === 4 && section3Complete)))) {
      setActiveSection(sectionNumber);
    }
  };

  // Función para obtener el nombre del usuario
  const getUserDisplayName = () => {
    if (!session?.user) return '';
    return session.user.user_metadata?.full_name ||
      session.user.email?.split('@')[0] ||
      'Usuario';
  };

  // Función para obtener la inicial del usuario
  const getUserInitial = () => {
    const displayName = getUserDisplayName();
    return displayName ? displayName.charAt(0).toUpperCase() : '?';
  };

  // Estados para rastrear el progreso de generación de cada componente
  const [cvGenerated, setCvGenerated] = useState(false);
  const [letterGenerating, setLetterGenerating] = useState(false);
  const [letterGenerated, setLetterGenerated] = useState(false);
  const [analysisGenerating, setAnalysisGenerating] = useState(false);
  const [analysisGenerated, setAnalysisGenerated] = useState(false);

  const handleGenerateCV = async () => {
    if (!cvText || !jobDescription) {
      toast.error('Por favor completa toda la información necesaria');
      return;
    }

    // Verificar si el usuario tiene suficientes monedas
    if (!session?.user) {
      toast.error('Debes iniciar sesión para generar un CV');
      return;
    }

    console.log(`[CV-DEBUG] Iniciando generación de CV para usuario: ${session.user.id}`);
    console.log(`[CV-DEBUG] Verificando saldo de monedas...`);

    const hasCoins = await hasEnoughCoins(session.user.id, 100);
    if (!hasCoins) {
      // Mostrar el modal de recarga en lugar del mensaje de error
      setShowRechargeModal(true);
      return;
    }

    console.log(`[CV-DEBUG] Usuario tiene suficientes monedas, procediendo a descontar...`);

    // Obtener balance actual antes del descuento para depuración
    const balanceAntes = await getUserCoins(session.user.id);
    console.log(`[CV-DEBUG] Balance antes del descuento:`, balanceAntes);

    // Realizar el descuento de monedas
    try {
      // ¡IMPORTANTE! Forzamos un evento de carga/actualización antes del descuento
      // para asegurarnos de que la base de datos está sincronizada
      window.dispatchEvent(new CustomEvent('pre-coins-update'));

      // Pequeño retraso para asegurar que la sincronización tenga efecto
      await new Promise(resolve => setTimeout(resolve, 500));

      const descuentoRealizado = await updateUserCoins(
        session.user.id,
        -1, // Restamos 1 moneda
        'Generación de CV con IA' // Descripción de la transacción
      );

      console.log(`[CV-DEBUG] Resultado de descuento:`, descuentoRealizado);

      // Verificar que el descuento se haya realizado correctamente
      const balanceDespues = await getUserCoins(session.user.id);
      console.log(`[CV-DEBUG] Balance después del descuento:`, balanceDespues);

      if (!descuentoRealizado) {
        toast.error('No se pudieron descontar las monedas. Por favor, inténtalo más tarde.');
        return;
      }

      // Actualizar el balance local y notificar a otros componentes
      await loadUserCoins();
      console.log(`[CV-DEBUG] Disparando eventos de actualización...`);

      // Usar CustomEvent en lugar de Event para mejor compatibilidad (solo uno)
      window.dispatchEvent(new CustomEvent('coinsUpdated'));

      // Forzar actualización de componentes
      setRefreshTrigger(prev => prev + 1);

      // No mostrar toast aquí, ya se muestra en el componente de monedas

      // Pequeño retraso para asegurar que las actualizaciones visuales se han completado
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('[CV-DEBUG] Error al descontar monedas:', error);
      toast.error('Ocurrió un error al descontar las monedas. Por favor, inténtalo más tarde.');
      return;
    }

    try {
      // Resetear estados de generación
      setCvGenerated(false);
      setLetterGenerating(false);
      setLetterGenerated(false);
      setAnalysisGenerating(false);
      setAnalysisGenerated(false);

      setIsGenerating(true);
      setLoadingMessage('Preparando para optimizar tu CV con IA...');

      // Proceso de generación del CV con actualizaciones de estado
      setTimeout(() => setLoadingMessage('Analizando tu CV y extrayendo información clave...'), 500);

      // Análisis del empleo
      setLoadingMessage('Analizando requisitos del empleo...');
      const jobAnalysis = await getJobDetails(jobDescription);

      if (!jobAnalysis) {
        throw new Error('No se pudo analizar la información del empleo');
      }

      setLoadingMessage('Optimizando tu CV para este empleo específico...');

      // Generamos el CV optimizado (solo la primera parte)
      const initialResult = await generateOptimizedCV(
        jobUrl || 'No URL proporcionada',
        cvText,
        jobAnalysis
      );

      if (!initialResult || !initialResult.cv) {
        throw new Error('No se pudo generar el CV optimizado');
      }

      // Preparamos el resultado inicial con la plantilla y foto
      const cvResult = {
        ...initialResult,
        cv: {
          ...initialResult.cv,
          photo: (photo && photo.trim() !== '') ? photo : null,
          template: selectedTemplate,
          templateColor: selectedTemplateColor
        }
      };

      // Guardamos en localStorage los datos iniciales
      localStorage.setItem('cvData', JSON.stringify(cvResult));

      // Notificamos que el CV está listo
      setCvGenerated(true);

      // Navegamos a la vista previa
      navigate('/preview');

      // Generar nombre automático para el CV con IA
      const jobTitle = typeof jobAnalysis === 'string' ? jobAnalysis : jobAnalysis?.title || 'Sin título';
      const cvName = await generateCVName(jobTitle, undefined, cvResult?.cv?.name);

      // Extraer nombre de la empresa con IA (si existe en la descripción)
      let companyName: string | null = null;
      try {
        const { extractCompanyName } = await import('../lib/groq');
        companyName = await extractCompanyName(jobDescription || '');
        console.log('🏢 Empresa detectada:', companyName || 'No detectada');
      } catch (error) {
        console.log('No se pudo detectar empresa:', error);
      }

      // Guardar generación completa en base de datos
      let generationId: string | null = null;
      try {
        const result = await createCVGeneration(session.user.id, {
          cv_name: cvName,
          company_name: companyName || null,
          cv_data: cvResult.cv,
          job_description: jobDescription || undefined,
          template: selectedTemplate,
          template_color: selectedTemplateColor,
          photo_url: photo || undefined,
          coins_used: 1,
          status: 'completed',
        });

        if (result.success && result.data) {
          generationId = result.data.id;
          console.log('✅ Generación guardada en DB con nombre:', cvName);

          // Capturar preview después de guardar (3 segundos de espera para render)
          if (session?.user?.id) {
            setTimeout(async () => {
              try {
                const { captureCVPreview } = await import('../services/previewCapture');
                await captureCVPreview('cv-preview', session.user.id, generationId!);
                console.log('📸 Screenshot del CV capturado');
              } catch (error) {
                console.error('Error capturando preview:', error);
              }
            }, 3000);
          }
        }
      } catch (error) {
        console.error('❌ Error guardando generación:', error);
      }

      // En paralelo, generamos la carta de presentación
      setLetterGenerating(true);
      setTimeout(async () => {
        try {
          const coverLetter = await generateCoverLetter(cvResult.cv, jobAnalysis);

          // Actualizamos el resultado en localStorage con la carta
          const updatedResult = {
            ...cvResult,
            coverLetter
          };
          localStorage.setItem('cvData', JSON.stringify(updatedResult));

          setLetterGenerated(true);
          setLetterGenerating(false);

          // Actualizar en base de datos si tenemos el ID
          if (generationId) {
            try {
              await updateCVGeneration(generationId, {
                cover_letter: coverLetter
              });
              console.log('✅ Cover letter guardada en DB');
            } catch (error) {
              console.error('❌ Error actualizando cover letter:', error);
            }
          }

          // Enviamos evento personalizado para notificar a Preview que hay actualización
          window.dispatchEvent(new CustomEvent('coverLetterGenerated'));

          // Generamos el análisis de mejoras
          setAnalysisGenerating(true);
          const analysis = await generateImprovementAnalysis(cvResult.cv, jobAnalysis);

          // Actualizamos el resultado en localStorage con el análisis
          const finalResult = {
            ...updatedResult,
            analysis
          };
          localStorage.setItem('cvData', JSON.stringify(finalResult));

          setAnalysisGenerated(true);
          setAnalysisGenerating(false);

          // Actualizar en base de datos si tenemos el ID
          if (generationId) {
            try {
              await updateCVGeneration(generationId, {
                analysis: analysis
              });
              console.log('✅ Analysis guardado en DB');
            } catch (error) {
              console.error('❌ Error actualizando analysis:', error);
            }
          }

          // Enviamos evento personalizado para notificar a Preview que hay actualización
          window.dispatchEvent(new CustomEvent('analysisGenerated'));

          // Finalizamos el proceso de generación
          setIsGenerating(false);

        } catch (error) {
          console.error('Error al generar componentes adicionales:', error);
          // No mostramos toast porque el usuario ya está en otra pantalla
          setIsGenerating(false);
        }
      }, 1000); // Comenzamos a generar lo demás después de 1 segundo

    } catch (error) {
      console.error('Error al generar CV:', error);
      toast.error('Ocurrió un error al generar el CV. Por favor intenta nuevamente.');
      setIsGenerating(false);
    }
  };

  // Función para formatear el tamaño del archivo
  const formatFileSize = (bytes: number): string => {
    const sizeInKB = bytes / 1024;
    const sizeInMB = sizeInKB / 1024;

    if (sizeInMB >= 1) {
      return `${sizeInMB.toFixed(2)} MB`;
    } else {
      return `${sizeInKB.toFixed(0)} KB`;
    }
  };

  // Función para eliminar el CV cargado
  const handleDeleteCV = () => {
    // Limpiar todos los estados relacionados con el CV
    setCvText('');
    setFileName('');
    setFileType('');
    setFileSize('');
    setShowFullCvText(false);
    setSection1Complete(false);

    toast.success('CV eliminado correctamente. Puedes cargar uno nuevo.');
  };

  // Función para hacer scroll al botón siguiente o al mensaje del bot
  const scrollToNextSection = () => {
    setTimeout(() => {
      const nextButton = document.querySelector('[data-next-section]');
      if (nextButton) {
        nextButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500); // Aumentamos el tiempo de espera para asegurar que el DOM se ha actualizado
  };

  // Modificar handleFileUpload para incluir el autoscroll y cerrar la carpeta
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Cerrar la carpeta cuando se selecciona un archivo
      setFolderOpen(false);

      // Guardar información del archivo sin mostrar animación de carga
      setFileName(file.name);
      setFileType(file.type);
      setFileSize(formatFileSize(file.size));

      // Paso 1: Intentar extraer texto con el método tradicional
      let text = await extractTextFromFile(file);

      // Paso 2: Si no hay texto o es insuficiente, activar OCR
      if (!text || !hasValidText(text)) {
        console.log('⚠️ Texto insuficiente. Activando OCR...');

        // Mostrar toast informando que se está usando OCR
        toast.loading('🔍 Extrayendo texto con IA...', { id: 'ocr-loading' });

        try {
          const ocrResult = await extractTextFromCV(file);

          if (ocrResult.success && ocrResult.text) {
            text = ocrResult.text;
            toast.success(`✅ Texto extraído con OCR (${ocrResult.tokenUsage?.totalTokens || 0} tokens)`, {
              id: 'ocr-loading',
              duration: 3000
            });
          } else {
            toast.error('❌ No se pudo extraer texto: ' + (ocrResult.error || 'Error desconocido'), {
              id: 'ocr-loading'
            });
            // Permitir continuar con texto vacío si OCR falla
            text = '';
          }
        } catch (ocrError) {
          console.error('Error en OCR:', ocrError);
          toast.error('Error al usar OCR. Puedes continuar ingresando tu información manualmente.', {
            id: 'ocr-loading'
          });
          text = '';
        }
      } else {
        console.log('✅ Texto extraído directamente (sin costo de OCR)');
      }

      setCvText(text);
      setShowCvTextArea(false);

      // Guardar automáticamente el CV del usuario
      if (text && session?.user?.id) {
        await saveUserCV(session.user.id, text, file.name);
        console.log('💾 CV guardado automáticamente');
      }

      toast.success('¡Archivo cargado correctamente!');

      // Activar el scroll después de un breve retraso
      setTimeout(() => {
        scrollToNextSection();
      }, 1000);
    } catch (error) {
      console.error('Error al extraer texto del archivo:', error);
      toast.error('Error al procesar el archivo. Intenta con otro formato.');
    }
  };

  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = () => {
    if (fileType.includes('pdf')) {
      return <FileText className="w-8 h-8 text-red-400" />;
    } else if (fileType.includes('word') || fileType.includes('document') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return <FileText className="w-8 h-8 text-blue-400" />;
    } else if (fileType.includes('text') || fileName.endsWith('.txt') || fileName.endsWith('.rtf')) {
      return <FileCode className="w-8 h-8 text-green-400" />;
    } else {
      return <File className="w-8 h-8 text-purple-400" />;
    }
  };

  // Función para obtener el tipo de archivo para mostrar
  const getFileTypeLabel = () => {
    if (fileType.includes('pdf')) {
      return 'PDF';
    } else if (fileType.includes('word') || fileType.includes('document') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return 'Word';
    } else if (fileType.includes('text') || fileName.endsWith('.txt')) {
      return 'Texto';
    } else if (fileName.endsWith('.rtf')) {
      return 'RTF';
    } else {
      return 'Documento';
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') {
        setTempImage(e.target.result);
        setShowCropper(true);
      }
    };
    reader.readAsDataURL(file);

    toast.success('¡Foto cargada correctamente!');
  };

  const handleCropComplete = async (croppedImage: string) => {
    setPhoto(croppedImage);
    setShowCropper(false);
    setTempImage('');

    // Solo guardar como foto de perfil si el toggle está activado
    if (saveAsProfilePhoto && session?.user?.id) {
      try {
        // Convertir base64 a blob
        const response = await fetch(croppedImage);
        const blob = await response.blob();

        // Crear archivo con extensión
        const fileExt = 'jpg';
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `avatars/${session.user.id}/${fileName}`;

        // Eliminar foto anterior del Storage si existe
        const { data: existingFiles } = await supabase
          .storage
          .from('curriculab')
          .list(`avatars/${session.user.id}`);

        if (existingFiles && existingFiles.length > 0) {
          const filesToRemove = existingFiles.map(file => `avatars/${session.user.id}/${file.name}`);
          await supabase.storage.from('curriculab').remove(filesToRemove);
        }

        // Subir nueva foto al Storage
        const { error: uploadError } = await supabase.storage
          .from('curriculab')
          .upload(filePath, blob);

        if (uploadError) throw uploadError;

        // Obtener URL pública
        const { data: { publicUrl } } = supabase
          .storage
          .from('curriculab')
          .getPublicUrl(filePath);

        setPhoto(publicUrl);
        console.log('✅ Foto guardada en Storage - Se mostrará en el Header también');

        // También guardar en la tabla como respaldo
        await saveUserProfilePhoto(session.user.id, publicUrl, 'upload');

        toast.success('Foto guardada como foto de perfil');
      } catch (error) {
        console.error('Error al guardar foto:', error);
        toast.error('Error al guardar la foto de perfil');
      }
    } else {
      // Solo se usa para el CV, no se guarda como perfil
      toast.success('Foto cargada para el CV');
    }

    // Marcar sección como completa
    setSection3Complete(true);
  };

  // Función para manejar la extracción de información de la URL
  const handleUrlPaste = async () => {
    if (!jobUrl) {
      toast.error('Por favor ingresa una URL válida');
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage('Extrayendo información del empleo...');

      // Simulamos la extracción de información (en una app real, esto haría una llamada a la API)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Ejemplo de descripción extraída (en una app real, esto vendría de la API)
      const extractedDescription = `Descripción extraída de ${jobUrl}:\n\nBuscamos un profesional con experiencia en desarrollo web, conocimientos de React, Node.js y bases de datos. El candidato ideal debe tener habilidades de comunicación efectiva y capacidad para trabajar en equipo. Se valorará experiencia previa en proyectos similares.`;

      setJobDescription(extractedDescription);
      setIsLoading(false);
      toast.success('¡Información extraída correctamente!');
    } catch (error) {
      console.error('Error al extraer información:', error);
      setIsLoading(false);
      toast.error('Error al extraer información. Intenta nuevamente.');
    }
  };

  // Función para manejar la entrada manual de texto
  const handleManualInput = () => {
    setShowCvTextArea(true);
  };

  // Modificar handleSaveManualText para incluir el autoscroll
  const handleSaveManualText = () => {
    if (cvText.trim()) {
      setShowCvTextArea(false);
      toast.success('¡Texto guardado correctamente!');

      // Activar el scroll después de un breve retraso
      setTimeout(() => {
        scrollToNextSection();
      }, 1000);
    } else {
      toast.error('Por favor ingresa algún texto antes de guardar');
    }
  };

  const handleDeletePhoto = async () => {
    if (!session?.user?.id) return;

    try {
      // Eliminar del Storage
      const { data: existingFiles } = await supabase
        .storage
        .from('curriculab')
        .list(`avatars/${session.user.id}`);

      if (existingFiles && existingFiles.length > 0) {
        const filesToRemove = existingFiles.map(file => `avatars/${session.user.id}/${file.name}`);
        await supabase.storage.from('curriculab').remove(filesToRemove);
      }

      // Limpiar el estado
      setPhoto(null);
      toast.success('Foto eliminada correctamente');
      console.log('✅ Foto eliminada del Storage');
    } catch (error) {
      console.error('Error al eliminar foto:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  // Maneja actualizaciones de monedas para refrescar el historial
  const handleCoinsUpdated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Añade el event listener para actualizar monedas
  useEffect(() => {
    window.addEventListener('coinsUpdated', handleCoinsUpdated);
    return () => {
      window.removeEventListener('coinsUpdated', handleCoinsUpdated);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black pt-8">
      {(isLoading || isGenerating) && <LoadingAnimation message={loadingMessage} />}

      {/* Modal de recarga */}
      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        userId={session?.user?.id || ''}
      />

      {/* Banner de CV pre-cargado */}
      {showCvPreloadedBanner && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slideDown">
          <div className="bg-gray-900/95 backdrop-blur-md text-white px-5 py-2.5 rounded-lg shadow-2xl border border-purple-500/40 flex items-center gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse"></div>
            <span className="text-sm font-medium text-purple-100">CV cargado correctamente</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">


        {/* Título principal - Solo visible en la sección 1 */}
        {activeSection === 1 && (
          <div className="mb-8 text-center relative">
            <div className="absolute -left-6 -top-3 w-20 h-20 bg-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-xl"></div>

            <h1 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-100 mb-3">
              Crear tu CV con IA
            </h1>

            <div className="flex justify-center mb-3">
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
            </div>

            <p className="text-purple-200/80 max-w-2xl mx-auto">
              Sigue estos pasos para obtener un CV profesional optimizado con inteligencia artificial
            </p>
          </div>
        )}

        {/* Indicador de progreso - siempre visible */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div
              className={`flex flex-col items-center ${activeSection >= 1 ? 'text-purple-300' : 'text-gray-500'}`}
              onClick={() => section1Complete && handleSectionChange(1)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${activeSection === 1 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : activeSection > 1 ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                {activeSection > 1 ? '✓' : '1'}
              </div>
              <span className="text-xs">CV</span>
            </div>

            <div className={`w-full h-1 mx-1 ${activeSection >= 2 ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gray-700'}`}></div>

            <div
              className={`flex flex-col items-center ${activeSection >= 2 ? 'text-purple-300' : 'text-gray-500'}`}
              onClick={() => section1Complete && handleSectionChange(2)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${activeSection === 2 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : activeSection > 2 ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                {activeSection > 2 ? '✓' : '2'}
              </div>
              <span className="text-xs">Empleo</span>
            </div>

            <div className={`w-full h-1 mx-1 ${activeSection >= 3 ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gray-700'}`}></div>

            <div
              className={`flex flex-col items-center ${activeSection >= 3 ? 'text-purple-300' : 'text-gray-500'}`}
              onClick={() => section2Complete && handleSectionChange(3)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${activeSection === 3 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : activeSection > 3 ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                {activeSection > 3 ? '✓' : '3'}
              </div>
              <span className="text-xs">Foto</span>
            </div>

            <div className={`w-full h-1 mx-1 ${activeSection >= 4 ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gray-700'}`}></div>

            <div
              className={`flex flex-col items-center ${activeSection >= 4 ? 'text-purple-300' : 'text-gray-500'}`}
              onClick={() => section3Complete && handleSectionChange(4)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${activeSection === 4 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : activeSection > 4 ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                4
              </div>
              <span className="text-xs">Diseño</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* Paso 1: CV Actual - Solo visible cuando activeSection es 1 */}
          {activeSection === 1 && (
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/60 p-6 border border-purple-500/20 shadow-[0_8px_30px_rgba(138,75,175,0.2)]">
              {/* Efecto de resplandor en la esquina */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-blue-500/10 rounded-full blur-xl"></div>

              {/* Borde luminoso en la esquina */}
              <div className="absolute top-0 left-0 w-2/3 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
              <div className="absolute top-0 left-0 w-px h-2/3 bg-gradient-to-b from-transparent via-purple-500/50 to-transparent"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-lg font-medium text-white shadow-[0_0_10px_rgba(138,75,175,0.5)] cursor-pointer transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                    onClick={() => handleSectionChange(1)}
                  >
                    1
                  </span>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                    Ingresa tu CV actual
                  </h2>
                </div>

                {/* Texto explicativo del paso 1 - Versión simplificada */}
                <div className="mb-6 bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
                  <h3 className="text-lg font-medium text-blue-200 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Paso 1: Carga tu CV
                  </h3>
                  <p className="text-purple-100/80 text-sm leading-relaxed">
                    Puedes subir tu CV en formato PDF, Word (DOC/DOCX), TXT o RTF, o escribir/pegar el contenido manualmente.
                  </p>
                </div>

                {/* Sección de ejemplo y descarga de CV - COLLAPSIBLE */}
                <div className="mb-6 bg-indigo-900/40 rounded-lg border-2 border-indigo-500/30 shadow-lg">
                  <button
                    onClick={() => setShowHelpSection(!showHelpSection)}
                    className="w-full text-left p-4 flex items-center justify-between hover:bg-indigo-900/60 transition-colors rounded-lg"
                  >
                    <h3 className="text-base font-medium text-indigo-200 flex items-center">
                      <HelpCircle className="h-5 w-5 mr-2 text-indigo-300" />
                      ¿Tienes dudas sobre la estructura a cargar?
                    </h3>
                    <svg
                      className={`h-5 w-5 text-indigo-300 transition-transform ${showHelpSection ? 'rotate-180' : ''}`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {showHelpSection && (
                    <div className="px-4 pb-4">
                      <p className="text-purple-100/90 text-sm leading-relaxed mb-4">
                        Incluye: datos personales, experiencia laboral, formación y habilidades. Nuestra IA optimizará tu CV.
                      </p>
                      <a
                        href="/cv/cv_1.docx"
                        download
                        className="flex items-center text-sm text-blue-300 hover:text-blue-200 transition-colors bg-blue-900/40 rounded-lg p-3 border border-blue-600/30 hover:bg-blue-800/50 hover:border-blue-500/40"
                      >
                        <FileText className="h-5 w-5 mr-2 text-blue-300" />
                        <span>Descargar ejemplo de CV en Word</span>
                        <Download className="h-4 w-4 ml-2 text-blue-300" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Botones para cargar CV - solo visibles si no hay CV cargado o está en modo edición */}
                {(!cvText || showCvTextArea) && (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <label className="relative group cursor-pointer">
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept=".pdf,.doc,.docx,.txt,.rtf"
                        className="hidden"
                      />
                      <div className="h-36 rounded-lg bg-gradient-to-br from-blue-800/50 to-indigo-800/50 border border-purple-400/20 shadow-md flex flex-col items-center justify-center p-4 transition-all duration-300 group-hover:border-purple-400/40 group-hover:shadow-[0_0_15px_rgba(138,75,175,0.3)]">
                        <div className="w-12 h-12 rounded-full bg-blue-900/80 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
                          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Folder
                              size={0.5}
                              color="#00d8ff"
                              className="custom-folder"
                              isOpen={folderOpen}
                              onOpenChange={setFolderOpen}
                              resetAfterDelay={true}
                              resetDelay={3000}
                            />
                          </div>
                        </div>
                        <span className="text-base text-blue-100 font-medium">Subir archivo</span>
                        <span className="text-xs text-purple-200/70 mt-1">PDF, DOC, DOCX, TXT, RTF</span>
                      </div>
                    </label>

                    <button
                      onClick={handleManualInput}
                      className="relative group"
                    >
                      <div className="h-36 rounded-lg bg-gradient-to-br from-indigo-800/50 to-purple-800/50 border border-purple-400/20 shadow-md flex flex-col items-center justify-center p-4 transition-all duration-300 hover:border-purple-400/40 hover:shadow-[0_0_15px_rgba(138,75,175,0.3)]">
                        <div className="w-12 h-12 rounded-full bg-purple-900/80 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
                          <PenLine className="w-6 h-6 text-purple-400" />
                        </div>
                        <span className="text-base text-purple-100 font-medium">Escribir manualmente</span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Áreas de texto con estilo actualizado */}
                {showCvTextArea && (
                  <div className="mt-6">
                    <textarea
                      value={cvText}
                      onChange={(e) => setCvText(e.target.value)}
                      placeholder="Ingresa o pega tu CV aquí..."
                      className="w-full h-64 p-4 rounded-lg bg-white/5 backdrop-blur-md border border-white/10 focus:border-purple-500/50 outline-none transition-colors shadow-inner text-white resize-none placeholder-purple-200/30"
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleSaveManualText}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg transition-all duration-300 shadow-[0_0_10px_rgba(138,75,175,0.3)] hover:shadow-[0_0_15px_rgba(138,75,175,0.5)] flex items-center gap-2"
                      >
                        <span>Guardar</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Vista previa actualizada con información del archivo */}
                {cvText && !showCvTextArea && (
                  <div id="cv-preview-container" className="mt-6 bg-white/5 backdrop-blur-md rounded-lg border border-white/10 overflow-hidden">
                    {/* Información del archivo si fue cargado */}
                    {fileName && (
                      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
                        <div className="flex items-center space-x-3 overflow-hidden flex-1">
                          {getFileIcon()}
                          <div className="overflow-hidden">
                            <h3 className="text-white font-medium text-sm truncate" title={fileName}>
                              {fileName}
                            </h3>
                            <div className="flex items-center text-xs text-purple-200/60 mt-0.5">
                              <span className="px-1.5 py-0.5 bg-purple-500/20 rounded text-purple-300 mr-2">
                                {getFileTypeLabel()}
                              </span>
                              <span>{fileSize}</span>
                            </div>
                          </div>
                        </div>
                        {/* Botones de acción para archivo cargado */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setShowCvTextArea(true)}
                            className="p-1.5 text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded-full transition-colors"
                            title="Editar texto extraído"
                            aria-label="Editar texto extraído"
                          >
                            <PenLine size={16} />
                          </button>
                          {/* Botón Eliminar reemplazado por X */}
                          <button
                            onClick={handleDeleteCV}
                            className="p-1.5 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-full transition-colors"
                            title="Eliminar CV"
                            aria-label="Eliminar CV"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-purple-200/70">
                          {fileName ? 'Contenido extraído' : 'Vista previa del texto'}
                        </span>
                        {/* Botones de acción para texto manual */}
                        {!fileName && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowCvTextArea(true)}
                              className="p-1.5 text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded-full transition-colors"
                              title="Editar texto"
                              aria-label="Editar texto"
                            >
                              <PenLine size={16} />
                            </button>
                            {/* Botón Eliminar reemplazado por X */}
                            <button
                              onClick={handleDeleteCV}
                              className="p-1.5 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-full transition-colors"
                              title="Eliminar contenido"
                              aria-label="Eliminar contenido"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="bg-black/20 rounded p-3 border border-purple-500/10">
                        <p className={`text-gray-300 text-sm ${showFullCvText ? '' : 'line-clamp-3'}`}>
                          {cvText}
                        </p>
                        {cvText && cvText.length > 0 && (
                          <button
                            onClick={() => setShowFullCvText(!showFullCvText)}
                            className="mt-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                          >
                            {showFullCvText ? 'Ocultar' : 'Mostrar más'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bot amigable para la sección 1 */}
                {section1Complete && (
                  <div className="space-y-4 mt-6 animate-fadeIn" data-next-section>
                    <FriendlyBot message="¡Excelente! He recibido el texto de tu CV. Ahora puedes continuar al siguiente paso para ingresar los detalles del empleo al que quieres aplicar." />

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleSectionChange(2)}
                        className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm rounded-md transition-all duration-300 shadow-[0_0_8px_rgba(138,75,175,0.3)] hover:shadow-[0_0_12px_rgba(138,75,175,0.5)] flex items-center gap-1.5"
                      >
                        <span>Siguiente paso</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paso 2: Detalles del empleo - Solo visible cuando activeSection es 2 */}
          {activeSection === 2 && (
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/60 p-6 border border-purple-500/20 shadow-[0_8px_30px_rgba(138,75,175,0.2)]">
              {/* Efecto de resplandor en la esquina */}
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-purple-500/10 rounded-full blur-xl"></div>

              {/* Borde luminoso en la esquina */}
              <div className="absolute top-0 right-0 w-2/3 h-px bg-gradient-to-l from-transparent via-purple-500/50 to-transparent"></div>
              <div className="absolute top-0 right-0 w-px h-2/3 bg-gradient-to-b from-transparent via-purple-500/50 to-transparent"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-lg font-medium text-white shadow-[0_0_10px_rgba(138,75,175,0.5)] cursor-pointer transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                    onClick={() => handleSectionChange(2)}
                  >
                    2
                  </span>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                    Detalles del empleo
                  </h2>
                </div>

                {/* Texto explicativo del paso 2 - Versión simplificada */}
                <div className="mb-6 bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
                  <h3 className="text-lg font-medium text-blue-200 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Paso 2: Detalles del empleo
                  </h3>
                  <p className="text-purple-100/80 text-sm leading-relaxed">
                    Introduce la descripción del puesto al que quieres aplicar. Esta información será utilizada para generar una carta de presentación personalizada y un análisis detallado de tus fortalezas y debilidades para el puesto.
                  </p>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="text-purple-200/80 text-sm font-medium mb-2 block">Descripción del puesto</label>
                    <textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Escribe o pega aquí la descripción completa del puesto de trabajo..."
                      className="w-full h-64 p-4 rounded-lg bg-white/5 backdrop-blur-md border border-white/10 focus:border-purple-500/50 outline-none transition-colors shadow-inner text-white placeholder-purple-200/30 resize-none"
                    />
                  </div>
                </div>

                {/* Bot amigable para la sección 2 */}
                {section2Complete && (
                  <div className="space-y-4 mt-6 animate-fadeIn">
                    <FriendlyBot message="¡Perfecto! Ya tengo la información del empleo. Ahora necesitamos añadir una foto profesional a tu CV para hacerlo más personal." />

                    <div className="flex justify-between">
                      <button
                        onClick={() => handleSectionChange(1)}
                        className="px-3 py-1.5 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm rounded-md transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Anterior</span>
                      </button>

                      <button
                        onClick={() => handleSectionChange(3)}
                        className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm rounded-md transition-all duration-300 shadow-[0_0_8px_rgba(138,75,175,0.3)] hover:shadow-[0_0_12px_rgba(138,75,175,0.5)] flex items-center gap-1.5"
                      >
                        <span>Siguiente paso</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paso 3: Foto - Solo visible cuando activeSection es 3 */}
          {activeSection === 3 && (
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/60 p-6 border border-purple-500/20 shadow-[0_8px_30px_rgba(138,75,175,0.2)]">
              {/* Efecto de resplandor en la esquina */}
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
              <div className="absolute -top-8 -left-8 w-28 h-28 bg-blue-500/10 rounded-full blur-xl"></div>

              {/* Borde luminoso en la esquina */}
              <div className="absolute bottom-0 right-0 w-2/3 h-px bg-gradient-to-l from-transparent via-purple-500/50 to-transparent"></div>
              <div className="absolute bottom-0 right-0 w-px h-2/3 bg-gradient-to-t from-transparent via-purple-500/50 to-transparent"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-lg font-medium text-white shadow-[0_0_10px_rgba(138,75,175,0.5)] cursor-pointer transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                    onClick={() => handleSectionChange(3)}
                  >
                    3
                  </span>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                    Foto de perfil (Opcional)
                  </h2>
                </div>

                {/* Texto explicativo del paso 3 - Versión simplificada */}
                <div className="mb-6 bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
                  <h3 className="text-lg font-medium text-blue-200 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Paso 3: Foto profesional (Opcional)
                  </h3>
                  <p className="text-purple-100/80 text-sm leading-relaxed">
                    Tu foto de perfil se utilizará en el CV. Puedes cambiarla o continuar sin foto.
                  </p>
                </div>

                {/* Layout de foto con botones de acción */}
                <div className="flex flex-col items-center gap-6">
                  {/* Vista previa de la foto */}
                  <div className="relative group">
                    {/* Anillo decorativo animado */}
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-75 blur-sm group-hover:opacity-100 transition-opacity"></div>

                    <div className="w-40 h-40 rounded-full overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-purple-500/30 relative">
                      {photo ? (
                        <img
                          src={photo}
                          alt="Foto de perfil"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-800/90 to-gray-900/90">
                          <div className="w-16 h-16 rounded-full bg-indigo-900/80 flex items-center justify-center mb-3">
                            <ImagePlus className="w-8 h-8 text-blue-400" />
                          </div>
                          <span className="text-sm text-purple-200/80">Sin foto</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Botones de acción circulares */}
                  <div className="flex items-center justify-center gap-4">
                    {/* Input file oculto */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload-step3"
                    />

                    {/* Botón circular para cambiar/subir foto */}
                    <div className="flex flex-col items-center gap-2">
                      <label
                        htmlFor="photo-upload-step3"
                        className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 flex items-center justify-center cursor-pointer transition-all duration-300 shadow-lg hover:shadow-purple-500/30 hover:scale-110"
                        title={photo ? 'Cambiar foto' : 'Subir foto'}
                      >
                        <ImagePlus className="w-5 h-5 text-white" />
                      </label>
                      <span className="text-xs text-purple-200/70">{photo ? 'Cambiar' : 'Subir'}</span>
                    </div>

                    {/* Botón circular toggle para guardar como perfil */}
                    <div className="flex flex-col items-center gap-2">
                      <button
                        onClick={() => setSaveAsProfilePhoto(!saveAsProfilePhoto)}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-110 ${saveAsProfilePhoto
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 ring-2 ring-green-400/50'
                            : 'bg-gray-700 hover:bg-gray-600'
                          }`}
                        title={saveAsProfilePhoto ? 'Se guardará como foto de perfil' : 'Solo para este CV'}
                      >
                        {saveAsProfilePhoto ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <span className={`text-xs ${saveAsProfilePhoto ? 'text-green-400' : 'text-gray-400'}`}>
                        {saveAsProfilePhoto ? 'Perfil ✓' : 'Solo CV'}
                      </span>
                    </div>

                    {/* Botón circular para eliminar foto (solo si hay foto) */}
                    {photo && (
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={handleDeletePhoto}
                          className="w-12 h-12 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-red-500/30 hover:scale-110"
                          title="Eliminar foto"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <span className="text-xs text-red-400/70">Eliminar</span>
                      </div>
                    )}
                  </div>

                  {/* Indicador del estado del toggle */}
                  <div className={`text-center p-2 rounded-lg text-sm ${saveAsProfilePhoto
                      ? 'bg-green-600/20 text-green-300 border border-green-500/30'
                      : 'bg-gray-700/30 text-gray-400 border border-gray-600/30'
                    }`}>
                    {saveAsProfilePhoto
                      ? '✓ La foto se guardará también como tu foto de perfil'
                      : 'La foto solo se usará en este CV'}
                  </div>

                  {/* Botón "Continuar sin foto" - Solo si no hay foto */}
                  {!photo && (
                    <button
                      onClick={() => {
                        setSection3Complete(true);
                        toast.success('Continuando sin foto. Podrás añadir una más tarde.');
                      }}
                      className="w-full max-w-md px-4 py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <span>Continuar sin foto</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}

                  {/* Recomendaciones compactas */}
                  <div className="w-full max-w-2xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-lg p-4 border border-indigo-500/20">
                    <h4 className="text-sm font-medium text-indigo-200 mb-3 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Tips para una foto profesional
                    </h4>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                        <span className="text-purple-200/80 text-xs">Fondo neutro</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                        <span className="text-purple-200/80 text-xs">Vestimenta profesional</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                        <span className="text-purple-200/80 text-xs">Buena iluminación</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                        <span className="text-purple-200/80 text-xs">Expresión amigable</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bot amigable para la sección 3 */}
                {section3Complete && (
                  <div className="space-y-4 mt-6 animate-fadeIn">
                    <FriendlyBot message={photo ? "¡Genial! Tu foto se ve muy profesional. Ahora vamos a elegir un diseño atractivo para tu CV que destaque tus habilidades." : "¡Perfecto! Has decidido continuar sin foto. No te preocupes, podrás añadir una más tarde si lo deseas. Ahora vamos a elegir un diseño atractivo para tu CV."} />

                    <div className="flex justify-between">
                      <button
                        onClick={() => handleSectionChange(2)}
                        className="px-3 py-1.5 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm rounded-md transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Anterior</span>
                      </button>

                      <button
                        onClick={() => handleSectionChange(4)}
                        className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm rounded-md transition-all duration-300 shadow-[0_0_8px_rgba(138,75,175,0.3)] hover:shadow-[0_0_12px_rgba(138,75,175,0.5)] flex items-center gap-1.5"
                      >
                        <span>Siguiente paso</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paso 4: Diseño - Solo visible cuando activeSection es 4 */}
          {activeSection === 4 && (
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/60 p-6 border border-purple-500/20 shadow-[0_8px_30px_rgba(138,75,175,0.2)]">
              {/* Efecto de resplandor en la esquina */}
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
              <div className="absolute -top-8 -right-8 w-28 h-28 bg-purple-500/10 rounded-full blur-xl"></div>

              {/* Borde luminoso en la esquina */}
              <div className="absolute bottom-0 right-0 w-2/3 h-px bg-gradient-to-l from-transparent via-purple-500/50 to-transparent"></div>
              <div className="absolute bottom-0 right-0 w-px h-2/3 bg-gradient-to-t from-transparent via-purple-500/50 to-transparent"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-lg font-medium text-white shadow-[0_0_10px_rgba(138,75,175,0.5)] cursor-pointer transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                    onClick={() => handleSectionChange(4)}
                  >
                    4
                  </span>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                    Elige un diseño
                  </h2>
                </div>

                {/* Texto explicativo del paso 4 - Versión simplificada */}
                <div className="mb-6 bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
                  <h3 className="text-lg font-medium text-blue-200 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Paso 4: Diseño del CV
                  </h3>
                  <p className="text-purple-100/80 text-sm leading-relaxed">
                    Selecciona una plantilla profesional que mejor se adapte al tipo de empleo al que estás aplicando.
                  </p>
                </div>

                <TemplateSelector
                  selectedTemplate={selectedTemplate}
                  onTemplateSelect={(template, color) => {
                    if (template.id) {
                      const isNewTemplate = selectedTemplate !== template.id;
                      setSelectedTemplate(template.id);
                      setSection4Complete(true);

                      // Actualizar el color seleccionado si se proporciona
                      if (color) {
                        setSelectedTemplateColor(color);
                        console.log("Dashboard - Color seleccionado actualizado a:", color);
                      }

                      // Solo mostrar toast si es un template nuevo, no al cambiar color
                      if (isNewTemplate) {
                        toast.success('¡Diseño seleccionado correctamente!');
                      }
                    }
                  }}
                  category={templateCategory}
                  userPhoto={photo} // Pasar la foto del usuario
                />

                {/* Bot amigable para la sección 4 - Solo visible cuando se selecciona un diseño */}
                {section4Complete && (
                  <div className="space-y-4 mt-6 animate-fadeIn">
                    <FriendlyBot message="¡Excelente elección de diseño! Has completado todos los pasos necesarios. Ahora puedes generar tu CV optimizado con IA haciendo clic en el botón 'Generar CV'." />

                    <div className="flex justify-between mb-12">
                      <button
                        onClick={() => handleSectionChange(3)}
                        className="px-3 py-1.5 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm rounded-md transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Anterior</span>
                      </button>
                    </div>

                    {/* Botón grande y centrado para generar CV con efecto arcoíris */}
                    <div className="flex justify-center">
                      <button
                        onClick={handleGenerateCV}
                        disabled={!cvText || !jobDescription || isGenerating}
                        className="rainbow-glow-button group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 text-lg font-medium"
                      >
                        {/* Resplandor arcoíris */}
                        <span className="absolute inset-0 -z-10 rounded-lg rainbow-border animate-rainbow-spin"></span>
                        <span className="absolute inset-0 -z-10 rounded-lg rainbow-glow blur-xl opacity-70 animate-pulse-slow"></span>

                        <Wand2 className="w-6 h-6 text-blue-200" />
                        <span>Generar CV con IA</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCropper && (
        <ImageCropper
          imageSrc={tempImage}
          onCancel={() => setShowCropper(false)}
          onCropComplete={handleCropComplete}
        />
      )}

      {/* Botón de optimización - ESTILO ACTUALIZADO - Solo visible cuando no está en la sección 3 ni 4 */}
      {activeSection !== 3 && activeSection !== 4 && section1Complete && section2Complete && section3Complete && (
        <div className="mt-12 text-center">
          <button
            onClick={() => handleSectionChange(4)}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm rounded-md transition-all duration-300 shadow-[0_0_8px_rgba(138,75,175,0.3)] hover:shadow-[0_0_12px_rgba(138,75,175,0.5)] flex items-center gap-1.5 mx-auto"
          >
            <span>Continuar al último paso</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}